/** QuickHouse — Décomposition murs + ouvertures en boîtes 3D (pur, sans three.js, unités cm) */
import { wallLength } from './geometry.js';

// ── Murs colinéaires : une ouverture peut chevaucher deux murs joints en ligne ──
/** Les deux murs sont-ils portés par la même droite ? */
export function areCollinear(w1, w2, tol = 1.5) {
  const len = wallLength(w1);
  if (len <= 0) return false;
  const nx = -(w1.b.y - w1.a.y) / len;
  const ny = (w1.b.x - w1.a.x) / len;
  const d = (p) => Math.abs((p.x - w1.a.x) * nx + (p.y - w1.a.y) * ny);
  return d(w2.a) <= tol && d(w2.b) <= tol;
}

/**
 * Ouvertures effectives d'un mur : les siennes + celles des murs COLINÉAIRES qui
 * débordent sur lui (offset recalculé dans son repère). Une ouverture posée près
 * d'un joint entre deux murs alignés est ainsi percée dans les deux.
 */
export function effectiveOpeningsForWall(wall, walls, openings) {
  const len = wallLength(wall);
  if (len <= 0) return [];
  const dir = { x: (wall.b.x - wall.a.x) / len, y: (wall.b.y - wall.a.y) / len };
  const out = [];
  for (const op of openings) {
    if (!op.wallId) continue; // velux
    if (op.wallId === wall.id) { out.push(op); continue; }
    const host = walls.find((w) => w.id === op.wallId);
    if (!host || host.hidden || !areCollinear(wall, host)) continue;
    const hlen = wallLength(host);
    if (hlen <= 0) continue;
    const t = op.offset / hlen;
    const c = { x: host.a.x + (host.b.x - host.a.x) * t, y: host.a.y + (host.b.y - host.a.y) * t };
    const u = (c.x - wall.a.x) * dir.x + (c.y - wall.a.y) * dir.y;
    if (u + op.width / 2 > 0.5 && u - op.width / 2 < len - 0.5) {
      out.push({ ...op, wallId: wall.id, offset: u });
    }
  }
  return out;
}

/**
 * L'ouverture tient-elle dans son mur, en comptant les murs colinéaires qui prolongent
 * la ligne ? Retourne true si l'intervalle [centre ± largeur/2] est couvert.
 */
export function openingCovered(op, host, walls, tol = 1) {
  const hlen = wallLength(host);
  if (hlen <= 0) return false;
  const dir = { x: (host.b.x - host.a.x) / hlen, y: (host.b.y - host.a.y) / hlen };
  const proj = (p) => (p.x - host.a.x) * dir.x + (p.y - host.a.y) * dir.y;
  // Intervalles couverts par le mur hôte et ses colinéaires, dans le repère de l'hôte
  const intervals = [];
  for (const w of walls) {
    if (w.id !== host.id && !areCollinear(host, w)) continue;
    const u1 = proj(w.a);
    const u2 = proj(w.b);
    intervals.push([Math.min(u1, u2), Math.max(u1, u2)]);
  }
  intervals.sort((p, q) => p[0] - q[0]);
  // Balayage de l'intervalle de l'ouverture
  let cursor = op.offset - op.width / 2 + tol;
  const end = op.offset + op.width / 2 - tol;
  for (const [i0, i1] of intervals) {
    if (i0 > cursor + tol) break; // trou dans la couverture
    if (i1 > cursor) cursor = i1;
    if (cursor >= end) return true;
  }
  return cursor >= end;
}

/**
 * Découpe un mur en boîtes pleines dans son repère local.
 * Retourne des specs { u, w, y0, h } :
 *   u  = début le long de l'axe (depuis a), w = largeur le long de l'axe,
 *   y0 = base verticale, h = hauteur.
 * Segments pleins entre ouvertures + linteau au-dessus + allège en dessous.
 * heightB optionnel : mur RAMPANT (hauteur variant linéairement de a à b) — facetté en bandes.
 * Une ouverture avec `arch` (flèche en cm) a un linteau parabolique (facetté).
 */
export function wallToBoxes(wall, openings, effectiveHeight, effectiveHeightB = null) {
  const len = wallLength(wall);
  const hA = effectiveHeight;
  const hB = effectiveHeightB ?? hA;
  if (len <= 0 || Math.max(hA, hB) <= 0) return [];

  const ops = openings
    .filter((o) => o.wallId === wall.id)
    .map((o) => ({
      u0: Math.max(0, o.offset - o.width / 2),
      u1: Math.min(len, o.offset + o.width / 2),
      top: o.sillHeight + o.height,
      sill: Math.max(0, o.sillHeight),
      center: o.offset,
      width: o.width,
      arch: o.arch ?? 0,
    }))
    .filter((o) => o.u1 > o.u0)
    .sort((p, q) => p.u0 - q.u0);

  const sloped = hA !== hB;
  const arched = ops.some((o) => o.arch !== 0);

  // ── Cas exact : mur horizontal sans arche ──
  if (!sloped && !arched) {
    const H = hA;
    const boxes = [];
    let cursor = 0;
    for (const op of ops) {
      if (op.u0 > cursor) boxes.push({ u: cursor, w: op.u0 - cursor, y0: 0, h: H });
      const w = op.u1 - op.u0;
      const top = Math.min(H, op.top);
      if (top < H) boxes.push({ u: op.u0, w, y0: top, h: H - top });     // linteau
      if (op.sill > 0) boxes.push({ u: op.u0, w, y0: 0, h: op.sill });   // allège
      cursor = Math.max(cursor, op.u1);
    }
    if (cursor < len) boxes.push({ u: cursor, w: len - cursor, y0: 0, h: H });
    return boxes;
  }

  // ── Cas facetté : bandes verticales le long du mur ──
  const topAt = (u) => hA + ((hB - hA) * u) / len;
  const opAt = (u) => ops.find((o) => u >= o.u0 && u < o.u1);
  // Sommet de l'ouverture à l'abscisse u (profil parabolique de flèche `arch`, nul aux bords)
  const opTopAt = (op, u) => {
    const dx = (2 * (u - op.center)) / op.width; // ∈ [-1, 1]
    return op.top + op.arch * (1 - dx * dx);
  };

  const stripW = Math.min(15, Math.max(4, len / 80));
  const count = Math.max(1, Math.round(len / stripW));
  const boxes = [];
  const pushMerged = (box) => {
    // Fusionne avec la boîte précédente si même section verticale (continuité)
    const last = boxes[boxes.length - 1];
    if (last && Math.abs(last.u + last.w - box.u) < 1e-6
      && Math.abs(last.y0 - box.y0) < 0.6 && Math.abs(last.h - box.h) < 0.6) {
      last.w += box.w;
    } else {
      boxes.push(box);
    }
  };
  for (let i = 0; i < count; i++) {
    const u0 = (len * i) / count;
    const u1 = (len * (i + 1)) / count;
    const mid = (u0 + u1) / 2;
    const top = topAt(mid);
    if (top <= 0) continue;
    const op = opAt(mid);
    if (!op) {
      pushMerged({ u: u0, w: u1 - u0, y0: 0, h: top });
      continue;
    }
    const opTop = Math.min(top, opTopAt(op, mid));
    if (top > opTop) pushMerged({ u: u0, w: u1 - u0, y0: opTop, h: top - opTop }); // linteau
    if (op.sill > 0) pushMerged({ u: u0, w: u1 - u0, y0: 0, h: op.sill });         // allège
  }
  return boxes;
}

/** Intervalles pleins (toute hauteur au sol) d'un mur — utilisé par le rendu 2D */
export function wallSolidSpans(wall, openings) {
  const len = wallLength(wall);
  const ops = openings
    .filter((o) => o.wallId === wall.id)
    .map((o) => ({ u0: Math.max(0, o.offset - o.width / 2), u1: Math.min(len, o.offset + o.width / 2) }))
    .filter((o) => o.u1 > o.u0)
    .sort((p, q) => p.u0 - q.u0);
  const spans = [];
  let cursor = 0;
  for (const op of ops) {
    if (op.u0 > cursor) spans.push({ u0: cursor, u1: op.u0 });
    cursor = Math.max(cursor, op.u1);
  }
  if (cursor < len) spans.push({ u0: cursor, u1: len });
  return spans;
}

/**
 * Élévations des étages (cm), triées par level croissant.
 * elevation(i) = Σ des (height + slabThickness) des niveaux inférieurs.
 * Retourne [{ floorId, level, elevation }].
 */
export function floorElevations(floors, slabThickness) {
  const sorted = [...floors].sort((p, q) => p.level - q.level);
  const out = [];
  let elev = 0;
  for (const f of sorted) {
    out.push({ floorId: f.id, level: f.level, elevation: elev });
    elev += f.height + slabThickness;
  }
  return out;
}
